/* eslint-disable no-underscore-dangle */
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { NgxMaskDirective } from 'ngx-mask';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isNil } from '@app/utils';
import { QuestionMarkMaskDirective } from '../../directives/question-mark-mask.directive';

@Component({
	selector: 'formly-masked-input',
	template: `
		<input
			matInput
			[readonly]="props.readonly"
			[mask]="mask"
			[showMaskTyped]="props.showMaskTyped"
			[patterns]="props.customPatterns ?? props.patterns"
			[dropSpecialCharacters]="props.dropSpecialCharacters"
			[allowNegativeNumbers]="props.allowNegativeNumbers || false"
			[formControl]="formControl"
			[formlyAttributes]="field"
			[allowNegativeNumbers]="props.allowNegativeNumbers"
			[thousandSeparator]="props.thousandSeparator || ','"
			[questionMarkMask]="props?.questionMarkMask"
		/>
	`,
	standalone: true,
	imports: [MatInputModule, NgxMaskDirective, FormsModule, ReactiveFormsModule, FormlyModule, QuestionMarkMaskDirective],
})
export class FormlyMaskedInputComponent extends FieldType<FormlyFieldConfig> implements OnInit, OnDestroy, AfterViewInit {
	maskValue = '';
	private readonly destroy$ = new Subject();

	get mask(): string {
		return this.maskValue || this.props.maskString;
	}

	ngOnInit(): void {
		if (isNil(this.props?.questionMarkMask)) {
			if (this.props.mask instanceof Observable) {
				this.props.mask.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
					this.maskValue = value;
				});
			}

			if (typeof this.props.mask === 'string') {
				this.maskValue = this.props.mask;
			}
		} else {
			// Question Mark Mask Directive will manage Control Mask.
			this.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
				this.validateQuestionMarkMask(value);
			});
		}
	}

	ngAfterViewInit(): void {
		// This is a workaround to make sure the label floats correctly when the mask is applied.
		setTimeout(() => {
			this.formControl?.updateValueAndValidity();
		});
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	private validateQuestionMarkMask(value: string): void {
		// Clean up data changes that occur after the question mark directive.

		let newValue = isNil(value) || value?.length === 0 ? this.props?.questionMarkMask : value;
		if (newValue.length !== this.props?.questionMarkMask.length) {
			// If the value is not the same length as the mask, apply ? mask to ensure the template is valid.
			let cleanedValue = newValue.replace(/\./g, '');
			let tempValue = '';
			const chars = this.props.questionMarkMask.split('');
			for (const char of chars) {
				if (char === '.') {
					tempValue += '.';
				} else {
					tempValue += cleanedValue.charAt(0).length > 0 ? cleanedValue.charAt(0) : '?';
					cleanedValue = cleanedValue.substring(1);
				}
			}
			newValue = tempValue;
		}
		if (/[a-z]/.test(newValue)) {
			// Ensure all characters are uppercase.
			newValue = newValue.toUpperCase();
		}
		if (value !== newValue) {
			this.formControl.setValue(newValue, { emitEvent: true });
		}
	}
}
